import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { C } from "./consumer/data";
import DebitOrderForm from "./consumer/DebitOrderForm";

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });

export default function DebitOrderPage() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | intro | form | done | error | signed
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/debit-order/${token}`)
      .then(({ data }) => { setInfo(data); setState(data.already_signed ? "signed" : "intro"); })
      .catch((e) => { setErr(e.response?.data?.detail || "This link isn't valid."); setState("error"); });
  }, [token]);

  const submit = async (f) => {
    await api.post(`/debit-order/${token}`, f);
    toast.success("Debit order received", { description: "Thank you — your cover continues uninterrupted." });
    setState("done");
  };

  const wrap = (children) => (
    <div style={{ minHeight: "100dvh", background: `linear-gradient(160deg,#0d1826 0%,#0B1E3B 55%,#12263f 100%)`, display: "flex", justifyContent: "center", fontFamily: "'Inter', system-ui, sans-serif" }} data-testid="debit-order-page">
      <div style={{ width: "100%", maxWidth: 560, background: C.body, minHeight: "100dvh" }}>
        <div style={{ background: C.navy, padding: "22px 20px" }}>
          <div style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 24, fontWeight: 700, color: "#fff", letterSpacing: "-0.02em" }}>ResQ1</div>
          <div style={{ fontSize: 12.5, color: "#B7C0CC", marginTop: 2 }}>Debit order authority</div>
        </div>
        <div style={{ padding: "22px 18px 48px" }}>{children}</div>
      </div>
    </div>
  );

  if (state === "loading") return wrap(<div style={{ display: "flex", alignItems: "center", gap: 10, color: C.slate }}><Loader2 size={18} style={{ animation: "rqspin 0.9s linear infinite" }} /> Loading…<style>{"@keyframes rqspin{to{transform:rotate(360deg)}}"}</style></div>);

  if (state === "error") return wrap(
    <div data-testid="do-error" style={{ textAlign: "center", padding: "40px 10px" }}>
      <div style={{ fontSize: 17, fontWeight: 700, color: C.ink, marginBottom: 6 }}>Link not valid</div>
      <p style={{ fontSize: 13.5, color: C.slate, lineHeight: 1.55 }}>{err} Please contact ResQ1 and we'll send you a fresh link.</p>
    </div>
  );

  if (state === "signed" || state === "done") return wrap(
    <div data-testid="do-done" style={{ textAlign: "center", padding: "40px 10px" }}>
      <div style={{ width: 64, height: 64, borderRadius: "50%", background: `${C.green}18`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
        <Check size={32} color={C.green} />
      </div>
      <div style={{ fontSize: 19, fontWeight: 800, color: C.ink, marginBottom: 6 }}>{state === "signed" ? "Already received" : "Thank you"}</div>
      <p style={{ fontSize: 14, color: C.slate, lineHeight: 1.6, maxWidth: 360, margin: "0 auto" }}>
        We've got your debit order authority{info?.first_name ? `, ${info.first_name}` : ""}. Your ResQ1 cover continues without interruption — there's nothing more you need to do.
      </p>
    </div>
  );

  if (state === "intro") return wrap(
    <div data-testid="do-intro">
      <h1 style={{ fontSize: 22, fontWeight: 800, color: C.ink, margin: "0 0 10px", letterSpacing: "-0.02em" }}>
        Hi {info?.first_name || "there"}, let's confirm your debit order
      </h1>
      <p style={{ fontSize: 14, color: C.slate, lineHeight: 1.6, margin: "0 0 16px" }}>
        Your ResQ1 emergency cover continues directly with us. To keep everything running smoothly we just
        need a fresh debit order authority. It takes about a minute, and your cover stays active throughout.
      </p>
      {[["Your cover never stops", "There's no gap — your panic button and response stay live."],
        ["Processed by Acuideas", "The same trusted partner that handles ResQ1 debit orders."],
        ["Secure & once-off", "Complete it once; we'll confirm by email when it's done."]].map(([t, d]) => (
        <div key={t} style={{ display: "flex", gap: 12, marginBottom: 12 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: `${C.red}14`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <ShieldCheck size={18} color={C.red} />
          </div>
          <div><div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{t}</div><div style={{ fontSize: 12.5, color: C.slate, marginTop: 1 }}>{d}</div></div>
        </div>
      ))}
      <button onClick={() => setState("form")} data-testid="do-start-btn"
        style={{ width: "100%", marginTop: 10, padding: 14, background: C.red, color: "#fff", border: 0, borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
        Complete my debit order
      </button>
    </div>
  );

  return wrap(
    <div>
      <h1 style={{ fontSize: 20, fontWeight: 800, color: C.ink, margin: "0 0 4px" }}>Debit order authority</h1>
      <p style={{ fontSize: 13, color: C.slate, margin: "0 0 16px" }}>Processed by Acuideas (Pty) Ltd on the 1st of each month.</p>
      <DebitOrderForm
        initial={{ name: (info?.full_name || "").split(" ")[0], surname: (info?.full_name || "").split(" ").slice(1).join(" "), mobile: info?.cell, id_number: info?.id_number, plan_code: info?.plan }}
        plans={info?.plans || []}
        submitLabel="Sign and submit" onSubmit={submit} />
    </div>
  );
}
